<section class="horizon-card" *ngIf="isLoading || hasLoadError || heatmap">
  <header class="horizon-card__head">
    <div>
      <h3 class="horizon-card__title">{{ title }}</h3>
      <p class="horizon-card__sub">{{ subtitle }}</p>
    </div>
  </header>

  <div class="hm-notice" *ngIf="isLoading && !heatmap" role="status">
    <i class="pi pi-spin pi-spinner hm-notice__icon" aria-hidden="true"></i>
    <p class="hm-notice__title">Loading the heat map…</p>
  </div>
  <div class="hm-notice hm-notice--error" *ngIf="hasLoadError" role="alert">
    <i class="fa-solid fa-circle-exclamation hm-notice__icon" aria-hidden="true"></i>
    <p class="hm-notice__title">The heat map could not be loaded</p>
    <p class="hm-notice__text">Check your connection and try again. If the problem continues, contact support.</p>
    <button type="button" class="_primary-btn" (click)="load()"><i class="fa-solid fa-rotate-right mr-2" aria-hidden="true"></i>Try again</button>
  </div>

  <div class="hm" *ngIf="heatmap" [ngClass]="'hm--zoom-' + zoom">
    <!-- Jumps the grid to a level. A Remmi project is one building, so with one building the levels are listed directly. -->
    <!-- Zoomed out, every level is on screen at once, so the jump list is not needed. -->
    <nav class="hm-buildings" aria-label="Jump to a level" *ngIf="!isZoomedOut">
      <small class="hm-buildings__hint" *ngIf="heatmap.buildings.length === 1">Jump to level</small>
      <div class="hm-building" *ngFor="let building of heatmap.buildings; trackBy: trackByBuilding">
        <button
          type="button"
          class="hm-building__head"
          *ngIf="heatmap.buildings.length > 1"
          [class.hm-building__head--active]="building.building_id === buildingId"
          [attr.aria-expanded]="openBuildingId === building.building_id"
          (click)="toggleBuilding(building.building_id)">
          {{ building.name }}
          <i [class]="openBuildingId === building.building_id ? 'pi pi-chevron-up' : 'pi pi-chevron-down'" aria-hidden="true"></i>
        </button>
        <ul class="hm-building__levels" *ngIf="heatmap.buildings.length === 1 || openBuildingId === building.building_id">
          <li *ngFor="let name of building.levels">
            <button
              type="button"
              class="hm-level"
              [class.hm-level--active]="building.building_id === buildingId && name === level"
              (click)="scrollToLevel(name)">
              {{ name }}
            </button>
          </li>
        </ul>
      </div>
    </nav>

    <div class="hm-level-view">
      <div class="hm-level-view__head">
        <h4 class="hm-level-view__title">{{ buildingName }}</h4>
        <div class="hm-floorplan" *ngIf="heatmap.floorplans?.length">
          <ng-select
            [items]="heatmap.floorplans"
            bindLabel="name"
            bindValue="id"
            placeholder="All floor plans"
            [searchable]="false"
            [clearable]="true"
            appendTo="body"
            [ngModel]="floorplanId"
            (ngModelChange)="setFloorplan($event)"></ng-select>
        </div>
        <ul class="hm-legend" aria-label="Legend">
          <li *ngFor="let item of legend"><i class="hm-dot" [ngClass]="'hm-dot--' + item.key"></i>{{ item.label }}</li>
        </ul>
        <!-- Zoom out to see every level at once: smaller tiles, no prices, no inner scroll. Colours stay. -->
        <div class="hm-zoom" role="group" aria-label="Heat map zoom">
          <button type="button" class="hm-zoom__btn" title="Zoom out" aria-label="Zoom out" [disabled]="zoom >= maxZoomOut" (click)="zoomOut()">
            <i class="pi pi-search-minus" aria-hidden="true"></i>
          </button>
          <button type="button" class="hm-zoom__btn" title="Zoom in" aria-label="Zoom in" [disabled]="zoom === 0" (click)="zoomIn()">
            <i class="pi pi-search-plus" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="hm-zoom__fit"
            [class.hm-zoom__fit--active]="isZoomedOut"
            [attr.aria-pressed]="isZoomedOut"
            (click)="toggleFitAll()">
            {{ isZoomedOut ? 'Normal view' : 'Fit all levels' }}
          </button>
        </div>
      </div>

      <!-- Every level, top floor first, in one box that scrolls; the list on the left jumps to a level. -->
      <div class="hm-levels" #levelsBox (scroll)="onLevelsScroll()">
        <section class="hm-levelblock" *ngFor="let section of sections; trackBy: trackByLevel" [attr.data-level]="section.level">
          <h5 class="hm-levelblock__title">{{ section.level }}</h5>

          <p class="hm-levelblock__note" *ngIf="section.isLoading">
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i> Loading {{ section.level }}…
          </p>
          <p class="hm-levelblock__note hm-levelblock__note--error" *ngIf="section.hasError">
            This level could not be loaded.
            <button type="button" class="horizon__link" (click)="retryLevel(section)">Try again</button>
          </p>
          <p class="hm-levelblock__note" *ngIf="!section.isLoading && !section.hasError && !section.lots.length">No lots on this level.</p>

          <div class="hm-scroll" *ngIf="section.lots.length">
            <div class="hm-grid" [style.--hm-columns]="section.columns.length">
              <span class="hm-grid__column" *ngFor="let column of section.columns; trackBy: trackByColumn">{{ column }}</span>
              <ng-container *ngFor="let column of section.columns; trackBy: trackByColumn">
                <ng-container *ngIf="lotAt(section, column) as lot; else emptyCell">
                  <button
                    type="button"
                    class="hm-lot"
                    [ngClass]="'hm-lot--' + stateOf(lot)"
                    [class.hm-lot--selected]="selectedLot?.unit_id === lot.unit_id"
                    [title]="lotTooltip(lot)"
                    (click)="selectLot(lot)">
                    <ng-container *ngIf="!isSold(lot); else soldLabel">
                      <strong>{{ lot.lot_name }}</strong>
                      <small *ngIf="!isZoomedOut">{{ shortPrice(lot.price) }}</small>
                    </ng-container>
                    <ng-template #soldLabel><small>SOLD</small></ng-template>
                  </button>
                </ng-container>
                <ng-template #emptyCell><span class="hm-lot hm-lot--empty"></span></ng-template>
              </ng-container>
            </div>
          </div>
        </section>
      </div>
      <p class="hm-note">Colour shows Horizon engagement. Sold lots stay grey whatever their activity.</p>

      <div class="hm-actions">
        <button type="button" class="_outline-btn horizon__icon-btn" (click)="viewFullMatrix()">
          View Full Matrix <i class="pi pi-arrow-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  </div>

  <div class="hm-lot-activity" *ngIf="selectedLot">
    <h4 class="hm-lot-activity__title">Lot {{ selectedLot.lot_name }} activity</h4>
    <app-horizon-lot-engagement [unitId]="selectedLot.unit_id" [leadId]="leadId"></app-horizon-lot-engagement>
  </div>
</section>
